import React, { useEffect, useMemo, useState } from "react"
import { Col, Nav, Row, Tab } from "react-bootstrap"
import moment from "moment"
import { useDispatch, useSelector } from "react-redux"
import * as echarts from 'echarts/core';

import useTranslation from "@/Hooks/UseTranslation"
import { useProject } from "@/Hooks/UseProject"

import IndicatorsService from "@/Services/IndicatorsService"
import InterfaceActions, { hideAsyncConfirm, setIsConfirming, showAsyncConfirm } from "@/Redux/Actions/InterfaceActions";

import ProjectLayout from "@/Layouts/MainLayout/ProjectLayout";
import ContentHeader from "@/Layouts/MainLayout/components/ContentHeader";
import ContentBody from "@/Layouts/MainLayout/components/ContentBody";
import ProjectOverviewHeader from "@/Pages/App/Project/Home/Components/ProjectOverviewHeader";
import ProjectIndicatorsNav from "@/Pages/App/Project/Home/Components/ProjectIndicatorsNav";

import WeeklyLineCard from "@/Components/Charts/Echarts/WeeklyLineCard/WeeklyLineCard";
import TableWithPieCard from "@/Components/Charts/Echarts/TableWithPieCard/TableWithPieCard";
import BasicColumnCard from "@/Components/Charts/ApexCharts/BasicColumnCard/BasicColumnCard";

const ProjectIndicatorsCNCPage = (props: any) => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { project, recalculateWeeklyProgress } = useProject()

  const indicatorsReducer = useSelector((state: any) => state.IndicatorsReducer)
  const loadProjectStatus = useSelector((state: any) => state.ProjectsReducer.loadRowStatus)

  const [tab, setTab] = useState('cnc')
  // TODO: add week navigator
  const [notCompleted, setNotCompleted] = useState([])
  const [weeklyCNCs, setWeeklyCNCs] = useState([])
  const [weeklyCNCGroups, setWeeklyCNCGroups] = useState([])

  const [ avoidRecalculate, setAvoidRecalculate ] = useState(false)

  const series = useMemo(() => {

    return [
      {
        data: notCompleted.map((item: any) => item.percentItems),
        name: t('INDICATORS_CNC:WeeklyNonCompletedItemsTooltip'),
        type: 'line',
        smooth: true,
        lineStyle: {
          normal: {
            color: '#ff5721',
            width: 2,
          }
        },
        itemStyle: {
          color: '#ff5721',
        },
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: '#ff572190'
            },
            {
              offset: 1,
              color: '#fff'
            }
          ])
        },
      },
      {
        data: notCompleted.map((item: any) => item.percentWorks),
        type: 'line',
        name: t('INDICATORS_CNC:WeeklyNonCompletedWorksTooltip'),
        smooth: true,
        lineStyle: {
          normal: {
            color: '#663299',
            width: 2,
          }
        },
        itemStyle: {
          color: '#663299',
        },
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: '#66329990'
            },
            {
              offset: 1,
              color: '#fff'
            }
          ])
        },
      },
    ]
  }, [notCompleted])

  // unused
  const getWeekTitle = () => {

    if (!indicatorsReducer.progressPercentHistory.weeks.length) {

      return null
    }

    const weekNumber = indicatorsReducer.progressPercentHistory.weeks[indicatorsReducer.progressPercentHistory.weeks.length - 1]

    const week = moment().week(weekNumber)

    return (
      <span>
        {t('INDICATORS_CNC:TitleWeek"')} {' '}
        {indicatorsReducer.progressPercentHistory.weeks.length} - {' '}
        {t('INDICATORS_CNC:TitleWeekFrom')} {' '}
        {week.startOf('week').format('DD/MM/YYYY')} {' '}
        {t('INDICATORS_CNC:TitleWeekTo')} {' '}
        {week.endOf('week').format('DD/MM/YYYY')}
      </span>
    )
  }

  useEffect(() => {

    const getWeeksData = async () => {

      const { data } = await IndicatorsService.getCNCsHistory(project._id)

      setNotCompleted(data)
    }

    const getWeeklyCNCs = async () => {

      const { data } = await IndicatorsService.getWeeklyCNCs(project._id)

      setWeeklyCNCs(data)
    }

    const getGroupedCNCTypesByWeek = async () => {

      const { data } = await IndicatorsService.getGroupedCNCTypesByWeek(project._id)

      setWeeklyCNCGroups(data)
    }

    if (!project._id) return

    if (project.mustRecalculateWeeklyProgress && !avoidRecalculate) return

    getWeeksData()
    getWeeklyCNCs()
    getGroupedCNCTypesByWeek()
  }, [project.mustRecalculateWeeklyProgress, project, avoidRecalculate])

  useEffect(() => {

    if (project.mustRecalculateWeeklyProgress && !avoidRecalculate) {

      const title = t('INDICATORS:ConfirmRecalculateWeeklyProgressTitle')
      const message = t('INDICATORS:ConfirmRecalculateWeeklyProgressMessage')

      dispatch(showAsyncConfirm({
        title,
        message,
        onConfirm: async () => {
          dispatch(setIsConfirming(true))

          await recalculateWeeklyProgress()

          dispatch(setIsConfirming(false))
          dispatch(hideAsyncConfirm())
        },
        onDeny: () => {
          setAvoidRecalculate(true)

          dispatch(hideAsyncConfirm())
        }
      }))
    }
  }, [project.mustRecalculateWeeklyProgress, avoidRecalculate, setAvoidRecalculate])

  useEffect(() => {

    const projectLoaded = loadProjectStatus == 'loaded'

    if (!projectLoaded || (project.mustRecalculateWeeklyProgress && !avoidRecalculate)) {

      dispatch(InterfaceActions.showLoader())
    } else {

      dispatch(InterfaceActions.hideLoader())
    }
  }, [loadProjectStatus, project.mustRecalculateWeeklyProgress, avoidRecalculate])

  return (
    <ProjectLayout menuName="start" subMenuName="indicators">
      <ProjectOverviewHeader activeTab="indicators" />
      <ContentHeader className="project-indicators-header" title={t('INDICATORS_CNC:Title')}>
        {/* <div className='ul-widget__head-label flex-grow-1'>
          <h3 className='ul-widget__head-title'>
            {t('INDICATORS_CNC:Title')}{' '}
            | <small style={{ fontSize: '70%' }}>
              {getWeekTitle()}
            </small>
          </h3>
        </div> */}
        <ProjectIndicatorsNav activeTab="cnc" />

      </ContentHeader>
      <ContentBody>
        <Row>
          <Col className="mb-4">
            <Tab.Content className="no-padding mb-2">
              <Tab.Pane active={tab === 'cnc'}>
                <div className="row mb-4">
                  <div className="col-md-12">
                    <WeeklyLineCard
                      title={t('INDICATORS_CNC:WeeklyNonCompletedTasks')}
                      tooltip={{
                        formatter: (items: any) => {

                          return `
                                      <div>
                                          ${items.map((item: any) => `<span>${item.seriesName}: ${item.value.toFixed(0)}</span>`).join('<br />')}
                                      </div>
                                  `
                        }
                      }}
                      series={series}
                      xAxisData={notCompleted.map((item: any, i: number) => i + 1)}
                      xAxisLabelFormatter="Sem {value}"
                      yAxisLabelFormatter="{value}"
                    />
                  </div>
                </div>
                <div className="row mb-4">
                  <div className="col-md-12">
                    <TableWithPieCard
                      title={t('INDICATORS_CNC:PieWeeklyCNCTypesTitle')}
                      data={weeklyCNCs}
                      tableFields={['CNC', 'Cantidad']}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <BasicColumnCard
                      title={t('INDICATORS_CNC:PieWeeklyCNCTitle')}
                      data={weeklyCNCGroups}
                    />
                  </div>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </ContentBody>
    </ProjectLayout>
  )
}

export default ProjectIndicatorsCNCPage
